import { useSuspenseQuery } from "@apollo/client";
import {
  Button,
  CashManagementStrategyChat,
  type CashManagementStrategyChatHandle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  OrganisationSelector,
} from "@repo/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { startTransition, useCallback, useRef, useState } from "react";
import { z } from "zod";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/cash-management-playground")({
  head: () => opsHead("Cash management playground"),
  validateSearch: z.object({
    threadId: z
      .string()
      .startsWith("cash_mgt_strategy_thread_")
      .optional()
      .catch(undefined),
  }),
  component: CashManagementPlayground,
});

const playgroundOrganisationsQuery = graphql(`
  query CashManagementPlaygroundOrganisations {
    organisations {
      id
      name
      squareLogoUrl
    }
  }
`);

function CashManagementPlayground() {
  const navigate = useNavigate();
  const { threadId } = Route.useSearch();
  const chatRef = useRef<CashManagementStrategyChatHandle>(null);
  const [organisationId, setOrganisationId] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [locallyCreatedThread, setLocallyCreatedThread] = useState<{
    id: string;
    previousThreadId?: string;
  }>();
  const [chatEpoch, setChatEpoch] = useState(0);
  const { data } = useSuspenseQuery(playgroundOrganisationsQuery);
  const organisations = data.organisations ?? [];
  const organisationName = organisations.find(
    (org) => org.id === organisationId,
  )?.name;
  const onThreadCreated = useCallback(
    (thread: { id: string }): void => {
      setLocallyCreatedThread({ id: thread.id, previousThreadId: threadId });
      startTransition(() => {
        void navigate({
          to: "/cash-management-playground",
          search: { threadId: thread.id },
          replace: true,
        });
      });
    },
    [navigate, threadId],
  );
  let chatThreadKey = threadId ?? "new";
  if (locallyCreatedThread && threadId === locallyCreatedThread.id) {
    chatThreadKey = locallyCreatedThread.previousThreadId ?? "new";
  }
  const chatKey = `${chatEpoch}:${chatThreadKey}`;

  return (
    <main className="flex h-[calc(100dvh-2rem)] min-h-0 shrink-0 flex-col overflow-hidden">
      {threadId && (
        <div className="flex justify-end border-region border-b px-4 py-2 shadow-surface">
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => {
              setOrganisationId(undefined);
              startTransition(() => {
                setLocallyCreatedThread(undefined);
                setChatEpoch((previous) => previous + 1);
                void navigate({
                  to: "/cash-management-playground",
                  search: {},
                  replace: true,
                });
              });
            }}
          >
            New chat
          </Button>
        </div>
      )}
      <Dialog open={!organisationId && !threadId}>
        <DialogContent requireIntentionalClosing hideCloseButton>
          <DialogHeader>
            <DialogTitle>Choose an organisation</DialogTitle>
            <DialogDescription>
              Select an organisation to begin its cashflow review and chat.
            </DialogDescription>
          </DialogHeader>
          {organisations.length ? (
            <OrganisationSelector
              organisations={organisations.map((org) => ({
                id: org.id,
                name: org.name,
                logoUrl: org.squareLogoUrl ?? null,
              }))}
              enabled
              onSelect={(selectedOrganisationId) => {
                setOrganisationId(selectedOrganisationId);
                chatRef.current?.startNewChat(selectedOrganisationId);
              }}
            />
          ) : (
            <p className="text-secondary text-sm">
              There are no active organisations available for this playground.
            </p>
          )}
        </DialogContent>
      </Dialog>
      <CashManagementStrategyChat
        key={chatKey}
        ref={chatRef}
        selectedThreadId={threadId}
        organisationId={organisationId}
        organisationName={organisationName}
        hasThreads
        onThreadCreated={onThreadCreated}
        onBusyChange={setBusy}
        onOrganisationIdChange={setOrganisationId}
        initialPrompt="Review this organisation’s three-month average cashflow and help me select its holding entity."
      />
    </main>
  );
}
